import React, { useState, useEffect, useMemo } from "react";
import {
  Box, Typography, Grid, Card, CardContent, TextField, Button, Divider,
  Table, TableHead, TableRow, TableCell, TableBody, Paper, Chip, Stack,
  TableContainer, IconButton, Tooltip,
  FormControl, InputLabel, MenuItem, Select,
} from "@mui/material";
import Swal from "sweetalert2";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs"; // Keep for date formatting
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AddCardIcon from '@mui/icons-material/AddCard';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import decodeJwtToken from "../auth/decodeJwtToken";
import { fetchSingleMemberDetails, submitMemberContribution, fetchMemberTransactions } from "../services/memberService";
import LoadingSpinner from "../components/LoadingSpinner";
import { fetchGroupTerms } from "../services/groupService";
import { fetchSystemSettings } from "../services/systemSettingsService";

const brand = "#800b33";
const brandHover = "#6c0a2b";

const normalizeListResponse = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.results)) return payload.results;
  if (Array.isArray(payload.data)) return payload.data;
  return [];
};

const MemberContribution = () => {
  const { t } = useTranslation();
  const [memberDetails, setMemberDetails] = useState(null);
  const [groupSettings, setGroupSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [contributionHistory, setContributionHistory] = useState([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [amount, setAmount] = useState("");
  const [terms, setTerms] = useState([]);
  const [selectedTermId, setSelectedTermId] = useState("current");

  const loggedInUser = decodeJwtToken(localStorage.getItem("accessToken"));
  const memberId = loggedInUser?.member_id; // Assuming member_id is in the token

  useEffect(() => {
    let active = true;
    fetchGroupTerms()
      .then((payload) => {
        if (!active) return;
        setTerms(normalizeListResponse(payload));
      })
      .catch((err) => console.error("Failed to load terms:", err));
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    fetchSystemSettings()
      .then((payload) => {
        if (!active) return;
        setGroupSettings(payload);
      })
      .catch((err) => console.error("Failed to load group settings:", err));
    return () => {
      active = false;
    };
  }, []);

  const activeTermId = useMemo(
    () => terms.find((term) => term.status === "ACTIVE")?.id || null,
    [terms]
  );
  const effectiveTermId = useMemo(() => {
    if (selectedTermId === "all") return null;
    if (selectedTermId === "current") return activeTermId || terms[0]?.id || null;
    return selectedTermId;
  }, [selectedTermId, activeTermId, terms]);
  const isCurrentTermView = useMemo(() => {
    if (selectedTermId === "current") return true;
    if (selectedTermId === "all") return false;
    if (activeTermId === null || activeTermId === undefined) return false;
    return String(selectedTermId) === String(activeTermId);
  }, [selectedTermId, activeTermId]);
  const contributionMode = groupSettings?.contribution_handling_mode || "PAYG_NO_PREPAY";
  const prepaidBalance = Number(memberDetails?.prepaid_contribution_balance || 0);

  const loadMemberDetails = async () => {
    if (!memberId) {
      setError(t("Member ID not found in token."));
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const data = await fetchSingleMemberDetails(memberId);
      setMemberDetails(data);
      setError(null);
    } catch (err) {
      setError(err.message);
      Swal.fire({
        icon: "error",
        title: t("Failed to load member details"),
        text: err.message,
        confirmButtonColor: brand,
      });
    } finally {
      setLoading(false);
    }
  };

  const loadTransactions = async () => {
    if (!memberId) return;
    if (selectedTermId === "current" && !effectiveTermId) return;
    try {
      setTransactionsLoading(true);
      const transactionParams = effectiveTermId ? { term_id: effectiveTermId } : {};
      if (process.env.NODE_ENV !== "production") {
        console.debug("MemberContribution transactions filter", transactionParams);
      }
      const transactions = await fetchMemberTransactions(memberId, transactionParams);
      setContributionHistory(normalizeListResponse(transactions));
    } catch (err) {
      console.error("Failed to load contributions:", err);
      Swal.fire({
        icon: "warning",
        title: t("Failed to load contributions"),
        text: err.message,
        confirmButtonColor: brand,
      });
      setContributionHistory([]);
    } finally {
      setTransactionsLoading(false);
    }
  };

  useEffect(() => {
    loadMemberDetails();
  }, [memberId, t]);

  useEffect(() => {
    loadTransactions();
  }, [memberId, effectiveTermId, selectedTermId]);

  const minimumPerPeriod = useMemo(() => {
    if (!memberDetails?.group) return null;
    const perPeriod = Number(memberDetails.group.contribution_amount ?? 0);
    const slots = Number(memberDetails.number_of_slots ?? 1);
    if (!Number.isFinite(perPeriod) || !Number.isFinite(slots)) return null;
    return perPeriod * slots;
  }, [memberDetails]);

  const periodWindow = useMemo(() => {
    if (!memberDetails?.next_due_date || !memberDetails?.group?.frequency) {
      return { start: null, end: null };
    }
    const end = dayjs(memberDetails.next_due_date);
    if (!end.isValid()) return { start: null, end: null };
    const start =
      memberDetails.group.frequency === "Weekly"
        ? end.subtract(7, "day")
        : end.subtract(1, "month");
    return { start, end };
  }, [memberDetails]);

  const paidForPeriod = useMemo(() => {
    if (!isCurrentTermView) return null;
    if (!periodWindow.start || !periodWindow.end) return null;
    return contributionHistory
      .filter((tx) => ["MEMBER_CONTRIBUTION", "CONTRIBUTION"].includes(tx.transaction_type))
      .reduce((sum, tx) => {
        const createdAt = dayjs(tx.created_at);
        if (!createdAt.isValid()) return sum;
        if (createdAt.isBefore(periodWindow.start, "day")) return sum;
        if (createdAt.isAfter(periodWindow.end, "day")) return sum;
        return sum + Math.abs(Number(tx.amount) || 0);
      }, 0);
  }, [contributionHistory, periodWindow, isCurrentTermView]);

  const remainingToMinimum = useMemo(() => {
    if (minimumPerPeriod === null || paidForPeriod === null) return null;
    const prepayOffset =
      contributionMode === "PREPAY_ADVANCE_ALLOWED"
        ? Math.min(prepaidBalance, minimumPerPeriod)
        : 0;
    return Math.max(minimumPerPeriod - (paidForPeriod + prepayOffset), 0);
  }, [minimumPerPeriod, paidForPeriod, contributionMode, prepaidBalance]);

  const periodLabel = useMemo(() => {
    if (!isCurrentTermView) return "—";
    if (!periodWindow.start || !periodWindow.end) return "—";
    return `${periodWindow.start.format("DD MMM")} - ${periodWindow.end.format("DD MMM")}`;
  }, [periodWindow, isCurrentTermView]);

  if (loading) return <LoadingSpinner />;
  if (error) {
    return (
      <Typography color="error" align="center" sx={{ my: 4 }}>
        {t("Error: {{message}}", { message: error })}
      </Typography>
    );
  }
  if (!memberDetails) {
    return (
      <Typography align="center" sx={{ my: 4 }}>
        {t("No member data available.")}
      </Typography>
    );
  }

  const ofPercent = (memberDetails.group?.default_df_percent || 0) / 100;
  const currentBalance = memberDetails.balance;
  const groupFrequency = memberDetails.group?.frequency;

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: (i = 1) => ({
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.1, duration: 0.4 },
    }),
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    Swal.fire({
      icon: 'success',
      title: t('Copied!'),
      toast: true,
      position: 'bottom-end',
      showConfirmButton: false,
      timer: 1500
    });
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "flex-end",
          justifyContent: "space-between",
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 900, color: brand, mb: 1 }}>
            {t("Group Contributions")}
          </Typography>
          <Typography variant="subtitle2" color="text.secondary">
            {t(
              "Contribute according to your group terms ({{frequency}}). Each deposit includes a {{percent}}% System Service Charge (SSC).",
              {
                frequency: memberDetails.group.frequency,
                percent: memberDetails.group.default_df_percent,
              }
            )}
          </Typography>
        </Box>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>{t("Term")}</InputLabel>
          <Select
            value={selectedTermId}
            label={t("Term")}
            onChange={(event) => setSelectedTermId(event.target.value)}
          >
            <MenuItem value="current">{t("Current term")}</MenuItem>
            <MenuItem value="all">{t("All terms")}</MenuItem>
            {terms.map((term) => (
              <MenuItem key={term.id} value={term.id}>
                {term.name || `${t("Term")} ${term.term_number}`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Grid container spacing={3}>
        {/* Left Form */}
        <Grid item xs={12} md={5}>
          <motion.div variants={fadeIn} initial="hidden" animate="visible">
            <Card sx={{ borderRadius: 3, boxShadow: 3, mb: 3 }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {t("Contribution Status")}
                  </Typography>
                  <Chip
                    size="small"
                    label={
                      contributionMode === "PREPAY_ADVANCE_ALLOWED"
                        ? t("Prepaid")
                        : t("Pay-As-You-Go")
                    }
                    color={contributionMode === "PREPAY_ADVANCE_ALLOWED" ? "success" : "default"}
                  />
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Stack spacing={1}>
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="body2" color="text.secondary">
                      {t("Current period")}
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {periodLabel}
                    </Typography>
                  </Box>
                  {!isCurrentTermView && (
                    <Typography variant="caption" color="text.secondary">
                      {t("Switch to the current term to view period progress.")}
                    </Typography>
                  )}
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="body2" color="text.secondary">
                      {t("Minimum due")}
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>
                      {minimumPerPeriod === null ? "—" : `${minimumPerPeriod.toLocaleString("en-US")} TZS`}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="body2" color="text.secondary">
                      {t("Paid so far")}
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>
                      {paidForPeriod === null ? "—" : `${paidForPeriod.toLocaleString("en-US")} TZS`}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="body2" color="text.secondary">
                      {t("Remaining to minimum")}
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      color={
                        remainingToMinimum === null
                          ? "text.primary"
                          : remainingToMinimum > 0
                            ? "warning.main"
                            : "success.main"
                      }
                    >
                      {remainingToMinimum === null ? "—" : `${remainingToMinimum.toLocaleString("en-US")} TZS`}
                    </Typography>
                  </Box>
                  {contributionMode === "PREPAY_ADVANCE_ALLOWED" ? (
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography variant="body2" color="text.secondary">
                        {t("Prepaid credit")}
                      </Typography>
                      <Typography variant="body2" fontWeight={700}>
                        {prepaidBalance.toLocaleString("en-US")} TZS
                      </Typography>
                    </Box>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      {t("Extra deposits stay as savings and do not prepay future periods.")}
                    </Typography>
                  )}
                </Stack>
              </CardContent>
            </Card>
            {/* Enhanced Payment Reference Card */}
            <Card sx={{ borderRadius: 3, boxShadow: 3, mb: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 700, color: brand, mb: 1 }}>
                  {t("Your Payment Reference")}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t("Use this unique reference for all NMB bank payments.")}
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f5f5f5' }}>
                  <Typography variant="h5" component="span" sx={{ fontWeight: 700, fontFamily: 'monospace' }}>
                    {memberDetails.payment_reference}
                  </Typography>
                  <Tooltip title={t("Copy Reference")}>
                    <IconButton onClick={() => handleCopy(memberDetails.payment_reference)}>
                      <ContentCopyIcon />
                    </IconButton>
                  </Tooltip>
                </Paper>
              </CardContent>
            </Card>

            {/* Group Details Card */}
            <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 700, color: brand, mb: 2 }}>
                  {t("Group Contribution Terms")}
                </Typography>
                <Stack spacing={1.5}>
                  <Typography>
                    <b>{t("Group:")}</b> {memberDetails.group.name}
                  </Typography>
                  <Typography>
                    <b>{t("Contribution:")}</b> {memberDetails.group.contribution_amount.toLocaleString()} TZS
                  </Typography>
                  <Typography>
                    <b>{t("Frequency:")}</b> {memberDetails.group.frequency}
                  </Typography>
                  <Typography>
                    <b>{t("System Service Charge (SSC):")}</b> {memberDetails.group.default_df_percent}%
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>

        {/* Right Summary + History */}
        <Grid item xs={12} md={7}>
          <motion.div variants={fadeIn} initial="hidden" animate="visible">
            {/* Enhanced Metrics Display */}
            <Paper sx={{ p: 2, borderRadius: 3, boxShadow: 3, mb: 3 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={6} sm={3} sx={{ textAlign: 'center' }}>
                  <AccountBalanceWalletIcon color="primary" sx={{ fontSize: 32, mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">{t("Current Balance")}</Typography>
                  <Typography variant="h6" fontWeight={700}>{currentBalance.toLocaleString()} TZS</Typography>
                </Grid>
                <Grid item xs={6} sm={3} sx={{ textAlign: 'center' }}>
                  <AddCardIcon color="success" sx={{ fontSize: 32, mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">{t("Overpayment")}</Typography>
                  <Typography variant="h6" fontWeight={700}>{memberDetails.overpayment_balance.toLocaleString()} TZS</Typography>
                </Grid>
                <Grid item xs={6} sm={3} sx={{ textAlign: 'center' }}>
                  <EventRepeatIcon color="action" sx={{ fontSize: 32, mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">{t("Prepaid Periods")}</Typography>
                  <Typography variant="h6" fontWeight={700}>{memberDetails.periods_covered_by_overpayment}</Typography>
                </Grid>
                <Grid item xs={6} sm={3} sx={{ textAlign: 'center' }}>
                  <EventAvailableIcon sx={{ fontSize: 32, mb: 1, color: brand }} />
                  <Typography variant="body2" color="text.secondary">{t("Next Due")}</Typography>
                  <Typography variant="h6" fontWeight={700} color={brand}>
                    {memberDetails.next_due_date ? dayjs(memberDetails.next_due_date).format("DD MMM YYYY") : t("N/A")}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>

            <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: brand }}>
                  {t("Contribution History")}
                </Typography>
                <TableContainer component={Paper} elevation={0}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>#</TableCell>
                        <TableCell>{t("Timestamp")}</TableCell>
                        <TableCell>{t("Type")}</TableCell>
                        <TableCell>{t("Amount")}</TableCell>
                        <TableCell>{t("Description")}</TableCell>
                        <TableCell>{t("Balance After")}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {transactionsLoading ? (
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                            {t("Loading contributions...")}
                          </TableCell>
                        </TableRow>
                      ) : contributionHistory.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                            {t("No transactions recorded yet.")}
                          </TableCell>
                        </TableRow>
                      ) : (
                        contributionHistory.map((txn, i) => (
                          <TableRow key={txn.id} hover>
                            <TableCell>{i + 1}</TableCell>
                            <TableCell>{dayjs(txn.created_at).format("YYYY-MM-DD HH:mm")}</TableCell>
                            <TableCell>
                              <Chip
                                label={t(txn.transaction_type)}
                                size="small"
                                color={txn.transaction_type === 'CONTRIBUTION' ? 'success' : 'default'}
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, color: txn.amount > 0 ? 'green' : 'inherit' }}>
                              {txn.amount.toLocaleString()}
                            </TableCell>
                            <TableCell>{txn.description}</TableCell>
                            <TableCell>{(txn.balance_after_transaction ?? 0).toLocaleString()}</TableCell>
                          </TableRow>
                        )))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>
      </Grid>
    </Box>
  );
};

export default MemberContribution;
